// import react hooks and material-ui components
import React, { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Logo from "assets/Logo.png";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import MenuIcon from "@mui/icons-material/Menu";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import { useNotifications } from "../../context/NotificationContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ListItemButton from "@mui/material/ListItemButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BellIcon from "assets/icons/Bell.svg";
import HelpIcon from "assets/icons/Help.svg";
import Cookies from "universal-cookie";
import Badge from "@mui/material/Badge";
import Settings from "@mui/icons-material/Settings";

const ResponsiveAppBar = ({ navItems }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const cookies = new Cookies();
  // track drawer open state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Safely access UserContext - it might not be available on public routes
  const userContext = useContext(UserContext);
  const userData = userContext?.userData || null;

  const PAYER_ROLES = ["LANDLORD", "PROPERTY_MANAGER", "HOMEOWNER", "STR_HOST"];

  // Get unread count from NotificationContext (safely handle if not available)
  let unreadCount = 0;
  try {
    const notifications = useNotifications();
    unreadCount = notifications?.unreadCount || 0;
    console.log("[ResponsiveAppBar] Current unread count:", unreadCount);
  } catch (error) {
    // NotificationContext not available (e.g., on public routes)
    unreadCount = 0;
    console.log(
      "[ResponsiveAppBar] NotificationContext not available, unreadCount:",
      unreadCount
    );
  }

  // track dropdown states
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  // track mobile dropdown expanded states
  const [mobileExpandedDropdowns, setMobileExpandedDropdowns] = useState(
    new Set()
  );

  // Add new state for profile dropdown
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const isProfileMenuOpen = Boolean(profileAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = () => {
    // Remove token cookie
    cookies.remove("token", { path: "/" });

    // Clear user context if available
    if (userContext?.setUserData) {
      userContext.setUserData(null);
    }

    // Redirect to login
    navigate("/login");

    handleProfileMenuClose();
  };

  // Handle scroll to section when navigating from another page
  useEffect(() => {
    if (location.state?.scrollToId) {
      const elementId = location.state.scrollToId;
      // Small delay to ensure the page has loaded
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const elementPosition = element.offsetTop - 80; // 80px offset
          window.scrollTo({
            top: elementPosition,
            behavior: "smooth",
          });
        }
      }, 100);

      // Clear the state to prevent re-scrolling on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Update active section when location changes
  useEffect(() => {
    // Check if any section is currently in view
    const checkActiveSection = () => {
      const sections = [
        "property-maintenance-homeowner",
        "end-to-end-repair-management-homeowner",
        "how-properteasy-works-homeowner",
        "pricing-homeowner",
        "what-makes-properteasy-different-homeowner",
        "faqs-homeowner",
        "stay-updated-homeowner",
        "property-maintenance-landlord",
        "end-to-end-repair-management-landlord",
        "how-properteasy-works-landlord",
        "pricing-landlord",
        "what-makes-properteasy-different-landlord",
        "faqs-landlord",
        "stay-updated-landlord",
        "property-maintenance-property-manager",
        "end-to-end-repair-management-property-manager",
        "how-properteasy-works-property-manager",
        "pricing-property-manager",
        "faqs-property-manager",
        "stay-updated-property-manager",
        "contact-property-managers",
        "property-maintenance-short-term-rentals",
        "end-to-end-repair-management-short-term-rentals",
        "how-properteasy-works-short-term-rentals",
        "pricing-short-term-rentals",
        "faqs-short-term-rentals",
        "contact-short-term-rentals",
        "what-makes-properteasy-different-short-term-rentals",
        "transparent-pricing-short-term-rentals",
        "grow-your-business",
        "verified-vendor-onboarding",
        "payout-stucture-vendor",
        "what-makes-properteasy-different-vendor",
        "faqs-vendor",
        "stay-updated-vendor",
      ];

      let activeSectionId = null;
      let maxVisibleRatio = 0;

      sections.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const visibleHeight =
            Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
          const visibleRatio = visibleHeight / rect.height;

          if (visibleRatio > maxVisibleRatio && visibleRatio > 0.3) {
            maxVisibleRatio = visibleRatio;
            activeSectionId = sectionId;
          }
        }
      });

      setActiveSection(activeSectionId);
    };

    // Debounced scroll handler
    let scrollTimeout;
    const debouncedCheckActiveSection = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkActiveSection, 100);
    };

    // Check on mount and scroll
    checkActiveSection();
    window.addEventListener("scroll", debouncedCheckActiveSection);

    return () => {
      window.removeEventListener("scroll", debouncedCheckActiveSection);
      clearTimeout(scrollTimeout);
    };
  }, [location.pathname]);

  // toggle mobile drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // handle dropdown open
  const handleDropdownOpen = (event, itemId) => {
    setDropdownAnchorEl(event.currentTarget);
    setActiveDropdown(itemId);
  };

  // handle dropdown close
  const handleDropdownClose = () => {
    setDropdownAnchorEl(null);
    setActiveDropdown(null);
  };

  // handle mobile dropdown toggle
  const handleMobileDropdownToggle = (itemId) => {
    setMobileExpandedDropdowns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // handle dropdown item click
  const handleDropdownItemClick = (link, parentItem) => {
    // Check if this dropdown item should scroll to a section or navigate to a route
    const dropdownItem = parentItem.dropdownItems.find(
      (item) => item.link === link
    );

    if (dropdownItem && dropdownItem.scroll) {
      // This is a section ID that needs to scroll to elements
      // Check if we're already on the correct page
      if (location.pathname === parentItem.link) {
        // We're on the same page, just scroll to the section
        const element = document.getElementById(link);
        if (element) {
          const elementPosition = element.offsetTop - 80; // 80px offset
          window.scrollTo({
            top: elementPosition,
            behavior: "smooth",
          });
        }
      } else {
        // We need to navigate to the parent page first, then scroll
        navigate(parentItem.link, {
          state: { scrollToId: link },
        });
      }
    } else {
      // This is a route navigation - navigate directly to the link
      navigate(link);
    }
    handleDropdownClose();
  };

  const buttonTabs = (navItems[userData?.accountType] || []).filter((item) => {
    if (item.type === "button") {
      if (userData) {
        // User is logged in, show dashboard/properties buttons
        if (item.label === "Get started") {
          item.label = "Dashboard";
          item.link = "/dashboard";
        }
        if (item.label === "Submit a request") {
          item.label = "Post a Job";
          item.link = "/post-job";
        }
      }
      return true; // Show all buttons
    }
    return false;
  });

  const handleButtonClick = (item) => {
    // Go directly to the link (which is now /login for unauthenticated users)
    navigate(item.link);
  };

  // mobile drawer contents
  const drawer = (
    <Box
      sx={{
        width: 280,
        padding: "20px 0",
        height: "100%",
        backgroundColor: "white",
        zIndex: 999,
      }}
    >
      <List>
        {(navItems[userData?.accountType] || navItems.general || []).map(
          (item) => {
            const hasDropdown =
              item.dropdownItems && item.dropdownItems.length > 0;
            const isExpanded = mobileExpandedDropdowns.has(item.id);

            // Check if any dropdown item is active
            const hasActiveDropdownItem =
              hasDropdown &&
              item.dropdownItems.some((dropdownItem) => {
                const isActive = dropdownItem.scroll
                  ? activeSection === dropdownItem.link
                  : location.pathname === dropdownItem.link;
                return isActive;
              });

            // Main item is active if either the item itself is active or any of its dropdown items are active
            const isMainItemActive =
              location.pathname === item.link || hasActiveDropdownItem;

            return (
              <React.Fragment key={item.id}>
                <ListItemButton
                  onClick={() => {
                    if (hasDropdown) {
                      // Toggle dropdown if item has dropdown items
                      handleMobileDropdownToggle(item.id);
                    } else {
                      // Navigate if no dropdown items
                      navigate(item.link);
                      setMobileOpen(false);
                    }
                  }}
                  sx={{
                    padding: "12px 24px",
                    "&:hover": {
                      backgroundColor: "#F6FFF7",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          onClick={(e) => {
                            if (item.link) {
                              e.stopPropagation();
                              navigate(item.link);
                              setMobileOpen(false);
                            }
                          }}
                          sx={{
                            cursor: "pointer",
                            fontSize: 16,
                            fontWeight: isMainItemActive ? 500 : 400,
                            color: isMainItemActive ? "#3CAB5C" : "#222",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {item.label}
                          {item.label === "Messages" && unreadCount > 0 && (
                            <Badge
                              badgeContent={unreadCount}
                              sx={{
                                "& .MuiBadge-badge": {
                                  backgroundColor: "#FF4444",
                                  color: "white",
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  minWidth: "16px",
                                  height: "16px",
                                  borderRadius: "8px",
                                  marginLeft: "10px",
                                },
                              }}
                            />
                          )}
                        </Typography>
                        {hasDropdown && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMobileDropdownToggle(item.id);
                            }}
                            sx={{
                              padding: "4px",
                              color: isMainItemActive ? "#3CAB5C" : "#222",
                            }}
                          >
                            <KeyboardArrowDownIcon
                              sx={{
                                fontSize: 16,
                                transform: isExpanded
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>

                {/* Dropdown items - only show when expanded */}
                {hasDropdown &&
                  isExpanded &&
                  item.dropdownItems.map((dropdownItem) => {
                    // Check if this dropdown item should be highlighted
                    // For scrolling items, check activeSection
                    // For route items, check if current path matches the link
                    const isActive = dropdownItem.scroll
                      ? activeSection === dropdownItem.link
                      : location.pathname === dropdownItem.link;

                    return (
                      <ListItemButton
                        key={dropdownItem.id}
                        onClick={() => {
                          handleDropdownItemClick(dropdownItem.link, item);
                          setMobileOpen(false);
                        }}
                        sx={{
                          padding: "8px 24px 8px 48px",
                          backgroundColor: isActive ? "#F6FFF7" : "transparent",
                          "&:hover": {
                            backgroundColor: "#F6FFF7",
                          },
                        }}
                      >
                        <ListItemText
                          primary={dropdownItem.label}
                          primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? "#3CAB5C" : "#666",
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
              </React.Fragment>
            );
          }
        )}

        {/* Add button tabs to mobile drawer */}
        {buttonTabs.map((item) => (
          <ListItemButton
            key={item.id}
            onClick={() => {
              navigate(item.link);
              setMobileOpen(false);
            }}
            sx={{
              padding: "12px 24px",
              "&:hover": {
                backgroundColor: "#F6FFF7",
              },
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: 16,
                fontWeight: 500,
                color: "#222",
              }}
            />
          </ListItemButton>
        ))}

        {/* Add logout and account links for mobile */}
        {userData && (
          <>
            {PAYER_ROLES.includes(userData.accountType) && (
              <ListItemButton
                onClick={() => {
                  navigate("/payment-methods");
                  setMobileOpen(false);
                }}
                sx={{
                  padding: "12px 24px",
                  marginTop: "8px",
                  borderTop: "1px solid #ECECEC",
                  "&:hover": {
                    backgroundColor: "#F6FFF7",
                  },
                }}
              >
                <ListItemText
                  primary="Payment Methods"
                  primaryTypographyProps={{
                    fontSize: 16,
                    fontWeight: 500,
                    color: "#222",
                  }}
                />
              </ListItemButton>
            )}
            <ListItemButton
              onClick={() => {
                handleLogout();
                setMobileOpen(false);
              }}
              sx={{
                padding: "12px 24px",
                marginTop: PAYER_ROLES.includes(userData.accountType)
                  ? "0px"
                  : "8px",
                borderTop: PAYER_ROLES.includes(userData.accountType)
                  ? "none"
                  : "1px solid #ECECEC",
                "&:hover": {
                  backgroundColor: "#F6FFF7",
                },
              }}
            >
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: "#DC3545",
                }}
              />
            </ListItemButton>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box
      data-testid="navbar"
      sx={{
        position: "fixed",
        top: 0,
        width: "100%",
        zIndex: 999,
      }}
    >
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: "white",
          boxShadow: "none",
          borderBottom: "1px solid #ECECEC",
          transition: "background-color 0.3s ease, box-shadow 0.3s ease",
          width: "100%",
          margin: "auto",
          paddingX: {
            xs: "16px",
            md: "24px",
          },
          paddingY: {
            xs: "16px",

            md: "16px",
          },
        }}
      >
        <Toolbar
          sx={{
            gap: {
              xs: "10px",
              sm: "15px",
              md: "20px",
              lg: "24px",
            },
            "@media (max-width:1124px)": {
              gap: "10px",
              justifyContent: "space-between",
            },
            "@media (max-width:768px)": {
              gap: "2px",
              justifyContent: "space-between",
            },
            paddingX: "0px !important",
            height: "100%",
            minHeight: "48px !important",
            maxWidth: "1392px",
            margin: "0 auto",
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{
                mr: 2,
                display: { md: "none" },
                color: "black",
                "@media (max-width:786px)": {
                  mr: 0,
                  paddingX: "0px",
                },
              }}
              onClick={handleDrawerToggle}
            >
              {/* <MenuIcon></MenuIcon> */}
            </IconButton>
            {location.pathname == "/post-job" ||
            location.pathname == "/vendor-onboarding" ? (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() =>
                  navigate(
                    location.pathname == "/post-job" ? "/dashboard" : "/"
                  )
                }
                sx={{
                  color: "#333333",
                  padding: "12px",
                  border: "1px solid#ECECEC",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "500",
                  lineHeight: "24px",
                }}
              >
                {location.pathname == "/post-job"
                  ? "Return to Dashboard"
                  : "Return to home page"}
              </Button>
            ) : (
              <img
                className=" h-[30px] "
                onClick={() =>
                  navigate(
                    userData?.accountType ? "/dashboard" : "/"
                  )
                }
                src={Logo}
                alt="Logo"
                style={{
                  height: 42,
                  cursor: "pointer",
                  filter: mobileOpen ? "" : "none",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: {
                xs: "none",
                md:
                  location.pathname == "/post-job" ||
                  location.pathname == "/vendor-onboarding"
                    ? "none"
                    : "flex",
              },
              flexGrow: 1,
              justifyContent: "center",
              alignItems: "center",
              gap: {
                md: "20px",
                lg: "32px",
                xl: "40px",
              },
            }}
          >
            <div className="flex gap-[10px] lg:gap-[20px] xl:gap-[32px]">
              {(navItems[userData?.accountType] || navItems.general || []).map(
                (item) => {
                  const active = location.pathname === item.link;
                  const hasDropdown =
                    item.dropdownItems && item.dropdownItems.length > 0;

                  // Check if any dropdown item is active
                  const hasActiveDropdownItem =
                    hasDropdown &&
                    item.dropdownItems.some((dropdownItem) => {
                      const isActive = dropdownItem.scroll
                        ? activeSection === dropdownItem.link
                        : location.pathname === dropdownItem.link;
                      return isActive;
                    });

                  // Main item is active if either the item itself is active or any of its dropdown items are active
                  const isMainItemActive = active || hasActiveDropdownItem;

                  return (
                    <Box key={item.id} sx={{ position: "relative" }}>
                      {hasDropdown ? (
                        <>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              onClick={(e) => {
                                if (item.link) {
                                  navigate(item.link);
                                } else {
                                  handleDropdownOpen(e, item.id);
                                }
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                fontWeight: isMainItemActive ? 500 : 400,
                                fontSize: 16,
                                color: isMainItemActive ? "#3CAB5C" : "#222",
                                "@media (max-width:1124px)": {
                                  fontSize: "12px",
                                },
                                "&:hover": {
                                  color: "#3CAB5C",
                                },
                              }}
                            >
                              {item.label}
                            </Typography>
                            <IconButton
                              onClick={(event) =>
                                handleDropdownOpen(event, item.id)
                              }
                              sx={{
                                padding: "4px",
                                marginLeft: "4px",
                                color: isMainItemActive ? "#3CAB5C" : "#222",
                                "&:hover": {
                                  color: "#3CAB5C",
                                },
                              }}
                            >
                              <KeyboardArrowDownIcon
                                sx={{
                                  fontSize: 16,
                                  transform:
                                    activeDropdown === item.id
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                  transition: "transform 0.2s ease",
                                }}
                              />
                            </IconButton>
                          </Box>
                          <Menu
                            anchorEl={dropdownAnchorEl}
                            open={activeDropdown === item.id}
                            onClose={handleDropdownClose}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                            disableScrollLock={true}
                            sx={{
                              "& .MuiPaper-root": {
                                borderRadius: "12px",
                                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                                border: "1px solid #ECECEC",
                                minWidth: "200px",
                              },
                            }}
                          >
                            {item.dropdownItems.map((dropdownItem) => {
                              // Check if this dropdown item should be highlighted
                              // For scrolling items, check activeSection
                              // For route items, check if current path matches the link
                              const isActive = dropdownItem.scroll
                                ? activeSection === dropdownItem.link
                                : location.pathname === dropdownItem.link;

                              return (
                                <MenuItem
                                  key={dropdownItem.id}
                                  onClick={() =>
                                    handleDropdownItemClick(
                                      dropdownItem.link,
                                      item
                                    )
                                  }
                                  sx={{
                                    padding: "12px 16px",
                                    fontSize: 14,
                                    color: isActive ? "#3CAB5C" : "#222",
                                    backgroundColor: isActive
                                      ? "#F6FFF7"
                                      : "transparent",
                                    fontWeight: isActive ? 500 : 400,
                                    "&:hover": {
                                      backgroundColor: "#F6FFF7",
                                      color: "#3CAB5C",
                                    },
                                  }}
                                >
                                  {dropdownItem.label}
                                </MenuItem>
                              );
                            })}
                          </Menu>
                        </>
                      ) : (
                        <Typography
                          onClick={() => navigate(item.link)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            cursor: "pointer",
                            fontWeight: active ? 500 : 400,
                            fontSize: 16,
                            color: active ? "#3CAB5C" : "#222",
                            "@media (max-width:1124px)": {
                              fontSize: "12px",
                            },
                          }}
                        >
                          {item.label}
                          {item.label === "Messages" && unreadCount > 0 && (
                            <Badge
                              badgeContent={unreadCount}
                              sx={{
                                "& .MuiBadge-badge": {
                                  backgroundColor: "#FF4444",
                                  color: "white",
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  minWidth: "16px",
                                  height: "16px",
                                  borderRadius: "8px",
                                  marginLeft: "10px",
                                },
                              }}
                            />
                          )}
                        </Typography>
                      )}
                    </Box>
                  );
                }
              )}
            </div>
          </Box>

          <Box
            sx={{
              display:
                location.pathname == "/post-job" ||
                location.pathname == "/vendor-onboarding"
                  ? "none"
                  : "flex",
              gap: "16px",
              justifyContent: "flex-end",
              alignItems: "center",
              flexShrink: 0,
              "@media (max-width:1124px)": {
                gap: "8px",
              },
              "@media (max-width:768px)": {
                gap: "4px",
              },
            }}
          >
            {/* Show menu icon on screens below md instead of button tabs */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
              }}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{
                  color: "black",
                  padding: "8px",
                }}
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Show button tabs only on md and above screens */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: "16px",
                justifyContent: "flex-end",
                alignItems: "center",
                flexShrink: 0,
                "@media (max-width:1124px)": {
                  gap: "8px",
                },
                "@media (max-width:768px)": {
                  gap: "4px",
                },
              }}
            >
              {location.pathname == "/" ||
              location.pathname == "/vendors" ||
              location.pathname == "/homeowners" ||
              location.pathname == "/property-managers" ||
              location.pathname == "/short-term-rentals" ||
              location.pathname == "/landlords" ? (
                <>
                  {" "}
                  <Button
                    sx={{
                      border: "1px solid #ECECEC",
                      backgroundColor: "#F8F8F8",
                      borderRadius: "12px",
                      fontWeight: 500,
                      fontSize: 16,
                      textTransform: "none",
                      padding: "12px",
                      fontFamily: "Albert Sans",
                      color: "#222",
                      "&:hover": {
                        backgroundColor: "#3CAB5C",
                        color: "#FFFFFF",
                      },
                    }}
                    variant="contained"
                    color="primary"
                    onClick={() => { const mapping = { '/vendors': '/signup?account_type=VENDOR', '/homeowners': '/signup?account_type=HOMEOWNER', '/property-managers': '/signup?account_type=PROPERTY_MANAGER', '/short-term-rentals': '/signup?account_type=STR_HOST', '/landlords': '/signup?account_type=LANDLORD' }; navigate(userData ? "/dashboard" : (mapping[location.pathname] || "/signup")); }}
                  >
                    Get started
                  </Button>
                  <Button
                    sx={{
                      backgroundColor: "#3CAB5C",
                      borderRadius: "12px",
                      fontWeight: 500,
                      fontSize: 16,
                      textTransform: "none",
                      padding: "12px",
                      fontFamily: "Albert Sans",
                      color: "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#3CAB5C",
                      },
                    }}
                    variant="contained"
                    color="primary"
                    onClick={() => navigate("/login")}
                  >
                    Login
                  </Button>
                </>
              ) : (
                <>
                  {/*<div className="border border-[#ECECEC] rounded-full">*/}
                  {/*  <img className="p-3" src={HelpIcon} alt="Help" />*/}
                  {/*</div>*/}
                  {/*<div className="border border-[#ECECEC] rounded-full">*/}
                  {/*  <img className="p-3" src={BellIcon} alt="Notifications" />*/}
                  {/*</div>*/}
                  <div>
                    <div
                      className="bg-[#3CAB5C] rounded-full w-12 h-12 flex items-center justify-center text-white font-medium text-lg cursor-pointer"
                      onClick={handleProfileMenuOpen}
                      aria-label="Open settings menu"
                      title="Settings"
                    >
                      <Settings sx={{ color: "#FFFFFF" }} />
                    </div>

                    <Menu
                      anchorEl={profileAnchorEl}
                      open={isProfileMenuOpen}
                      onClose={handleProfileMenuClose}
                      disableScrollLock
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      sx={{
                        "& .MuiPaper-root": {
                          borderRadius: "12px",
                          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #ECECEC",
                          minWidth: "160px",
                        },
                      }}
                    >
                      {/*<MenuItem*/}
                      {/*  onClick={() => {*/}
                      {/*    //navigate("/settings");*/}
                      {/*    handleProfileMenuClose();*/}
                      {/*  }}*/}
                      {/*>*/}
                      {/*  Settings*/}
                      {/*</MenuItem>*/}
                      <MenuItem
                        onClick={() => {
                          navigate("/dashboard");
                          handleProfileMenuClose();
                        }}
                      >
                        Dashboard
                      </MenuItem>
                      {PAYER_ROLES.includes(userData?.accountType) && (
                        <MenuItem
                          onClick={() => {
                            navigate("/payment-methods");
                            handleProfileMenuClose();
                          }}
                        >
                          Payment Methods
                        </MenuItem>
                      )}
                      <MenuItem onClick={handleLogout}>Logout</MenuItem>
                    </Menu>
                  </div>
                </>
              )}
              {/* {location.pathname == "/"
                ? buttonTabs.map((item) => (
                    <Button
                      key={item.id}
                      variant={item.variant}
                      onClick={() => handleButtonClick(item)}
                      sx={{
                        borderColor:
                          item.variant === "outlined"
                            ? mobileOpen
                              ? "#E6E6E6"
                              : "#ECECEC"
                            : undefined,
                        color:
                          item.variant === "outlined"
                            ? mobileOpen
                              ? "#222"
                              : "#242424"
                            : undefined,
                        background:
                          item.variant === "contained"
                            ? item.color || "#4DB05A"
                            : "transparent",
                        borderRadius: "12px",
                        fontWeight: 500,
                        fontSize: 16,
                        textTransform: "none",
                        padding: "8px 24px",

                        "@media (max-width:1124px)": {
                          padding: "8px 8px",
                        },

                        "@media (max-width:768px)": {
                          fontSize: "12px",
                          // padding: "8px 10px",
                        },
                        boxShadow: "none",
                        "&:hover": {
                          borderColor:
                            item.variant === "outlined" ? "#4DB05A" : undefined,
                          background:
                            item.variant === "contained"
                              ? "#399B46"
                              : "rgba(255,255,255,0.1)",
                        },
                        "&:focus": {
                          outline: "none",
                          borderColor:
                            item.variant === "outlined" ? "#4DB05A" : undefined,
                        },
                        "&:focus-visible": {
                          outline: "none",
                          borderColor:
                            item.variant === "outlined" ? "#4DB05A" : undefined,
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  ))
                : buttonTabs.map((item) => (
                    <Button
                      key={item.id}
                      variant={item.variant}
                      onClick={() => handleButtonClick(item)}
                      sx={{
                        borderColor:
                          item.variant === "outlined"
                            ? mobileOpen
                              ? "#E6E6E6"
                              : "#ECECEC"
                            : undefined,
                        color:
                          item.variant === "outlined"
                            ? mobileOpen
                              ? "#222"
                              : "#242424"
                            : undefined,
                        background:
                          item.variant === "contained"
                            ? item.color || "#4DB05A"
                            : "transparent",
                        borderRadius: "12px",
                        fontWeight: 500,
                        fontSize: 16,
                        textTransform: "none",
                        padding: "8px 24px",

                        "@media (max-width:1124px)": {
                          padding: "8px 8px",
                        },

                        "@media (max-width:768px)": {
                          fontSize: "12px",
                          // padding: "8px 10px",
                        },
                        boxShadow: "none",
                        "&:hover": {
                          borderColor:
                            item.variant === "outlined" ? "#4DB05A" : undefined,
                          background:
                            item.variant === "contained"
                              ? "#399B46"
                              : "rgba(255,255,255,0.1)",
                        },
                        "&:focus": {
                          outline: "none",
                          borderColor:
                            item.variant === "outlined" ? "#4DB05A" : undefined,
                        },
                        "&:focus-visible": {
                          outline: "none",
                          borderColor:
                            item.variant === "outlined" ? "#4DB05A" : undefined,
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  ))} */}
            </Box>
          </Box>

          <Drawer
            variant="temporary"
            anchor="left"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: 280,
                borderRight: "1px solid #ECECED",
                paddingTop: "72px",
              },
            }}
          >
            {drawer}
          </Drawer>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default ResponsiveAppBar;
